/**
 * 견적 금액 계산. 순수 함수 하나뿐이고 저장소·시간·난수를 모르니 테스트가 값만 본다.
 *
 * 항목 → 공종 → 그룹 → 관리비 → 절사 순으로 한 방향으로만 흐른다.
 *   자재 = round(수량 × 자재단가) · 노무 = round(수량 × 노무단가) · 금액 = 자재 + 노무
 *   SET 부모는 자기 단가를 무시하고 자식 합을 쓴다
 *   관리비 = floor((기본 + 스타일) × 관리비율) · 총액 = gross − 절사액
 *   부가세 별도(EXCL)면 gross − 절사액 이 공급가이고 총액 = 공급가 + 부가세다
 */

import { formatNumber } from "../../lib/format";
import { checked } from "../money/checked";
import {
  amountsFromSupply,
  amountsFromTotal,
  DEFAULT_VAT_MODE,
  type VatMode,
} from "../money/vat";
import { TradeGroup } from "../template/trades";

/** 절사 기본 단위 — 만원 아래를 깎는다. */
export const ROUNDING_UNIT = 10_000;

/** 관리비율을 만분율(bp) 정수로 다룬다. 0.145 같은 값이 부동소수로 1원 모자라는 것을 막는다. */
export const RATE_SCALE = 10_000;

export interface CalcItemInput {
  qty: number;
  material_unit_price: number;
  labor_unit_price: number;
  // 원가 단가(부가세 빼고 · 자재·노무 합친 값) · null/없음 = 미입력.
  cost_unit_price?: number | null;
  children?: CalcItemInput[] | null;
}

export interface CalcTradeInput {
  group: TradeGroup;
  items: CalcItemInput[];
}

export interface CalcQuoteInput {
  mgmt_fee_rate: number;
  rounding_adjustment?: number | null;
  // 판매 단가의 부가세 포함/별도 · 없으면 포함.
  vat_mode?: VatMode | null;
  trades: CalcTradeInput[];
}

export interface CalcAmounts {
  material_amount: number;
  labor_amount: number;
  amount: number;
}

export interface CalcItemResult extends CalcAmounts {
  /** 자식이 있으면 SET 부모다. 부모 단가는 금액에 반영되지 않는다. */
  is_set: boolean;
  // 원가 금액 = round(수량 × 원가 단가) · SET 은 자식 합 · 원가가 하나도 없으면 null.
  cost_amount: number | null;
  // 금액 > 0 인데 원가가 없는 줄 수(SET 은 자식에서 센다).
  cost_missing_count: number;
  children: CalcItemResult[];
}

export interface CalcTradeResult extends CalcAmounts {
  group: TradeGroup;
  // 입력된 원가 금액 합(미입력 줄은 빠진다 — 완전한지는 cost_missing_count).
  cost_amount: number;
  cost_missing_count: number;
  items: CalcItemResult[];
}

export interface CalcQuoteResult {
  trades: CalcTradeResult[];
  subtotal_basic: number;
  subtotal_style: number;
  mgmt_fee: number;
  gross: number;
  rounding_adjustment: number;
  // 항상 부가세 포함 총액 = supply_amount + vat_amount.
  total: number;
  vat_mode: VatMode;
  supply_amount: number;
  vat_amount: number;
  cost_total: number;
  cost_missing_count: number;
  // 공급가 − 원가 합 · 원가 미입력 줄이 하나라도 있으면 null(빠진 채 세면 이익이 부푼다).
  profit: number | null;
  // 이익 ÷ 공급가 (만분율) · 이익이 null 이거나 공급가가 0 이면 null.
  profit_rate_bp: number | null;
}

/**
 * 수량 × 단가를 원 단위 정수로. 수량이 1.5 처럼 소수일 수 있어 여기서만 반올림한다.
 *
 * **수량을 1/1000 정수로 올려 곱한다.** `Math.round(qty * price)` 로 바로 하면
 * 정확한 곱이 `.5` 인데 수량이 2진수로 안 떨어질 때 반올림이 뒤집힌다
 * (실측: `1.005 × 100` → float 100.49999999999999 → 100, 기대 101).
 * DB 열이 `numeric(14,3)` 이라 수량의 유효 자리는 어차피 소수 셋째까지다.
 */
const QTY_SCALE = 1_000;

const won = (qty: number, unitPrice: number): number =>
  checked(
    Math.round((Math.round(qty * QTY_SCALE) * unitPrice) / QTY_SCALE),
    "금액"
  );

const sum = (values: number[]): number =>
  checked(
    values.reduce((a, b) => a + b, 0),
    "합계"
  );

const amountsOf = (material: number, labor: number): CalcAmounts => ({
  material_amount: material,
  labor_amount: labor,
  amount: material + labor,
});

const costSumOf = (rows: { cost_amount: number | null }[]): number =>
  sum(rows.map((r) => r.cost_amount ?? 0));

const missingOf = (rows: { cost_missing_count: number }[]): number =>
  rows.reduce((a, r) => a + r.cost_missing_count, 0);

export const calcItem = (input: CalcItemInput): CalcItemResult => {
  const children = (input.children ?? []).map(calcItem);

  if (children.length > 0) {
    const base = amountsOf(
      sum(children.map((c) => c.material_amount)),
      sum(children.map((c) => c.labor_amount))
    );
    const cost_amount = children.every((c) => c.cost_amount === null)
      ? null
      : costSumOf(children);
    return {
      ...base,
      is_set: true,
      cost_amount,
      cost_missing_count: missingOf(children),
      children,
    };
  }

  const base = amountsOf(
    won(input.qty, input.material_unit_price),
    won(input.qty, input.labor_unit_price)
  );
  const cost = input.cost_unit_price ?? null;
  const cost_amount = cost === null ? null : won(input.qty, cost);
  return {
    ...base,
    is_set: false,
    cost_amount,
    cost_missing_count: cost_amount === null && base.amount > 0 ? 1 : 0,
    children: [],
  };
};

export const calcTrade = (input: CalcTradeInput): CalcTradeResult => {
  const items = input.items.map(calcItem);
  const base = amountsOf(
    sum(items.map((i) => i.material_amount)),
    sum(items.map((i) => i.labor_amount))
  );
  return {
    ...base,
    group: input.group,
    cost_amount: costSumOf(items),
    cost_missing_count: missingOf(items),
    items,
  };
};

/**
 * 관리비 = floor(소계 × 요율). 요율을 bp 정수로 올려 곱한 뒤 정수로 나눈다.
 * `sub * rate` 로 바로 곱하면 0.145·0.009 처럼 2진수로 안 떨어지는 요율에서 1원이 덜 나온다.
 */
export const mgmtFeeOf = (subtotal: number, rate: number): number =>
  checked(
    Math.floor(
      // **나누기 전에 본다.** 나눈 뒤에만 보면 곱한 값이 2^53 을 넘어도 결과는
      // 안전정수라 검사를 빠져나가고, 조용히 1원이 어긋난다
      // (실측: 소계 1,000,000,000,000,055 × 14.5% → 145,000,000,000,008,
      //  정확값 145,000,000,000,007). 스키마 상한이 이 구간을 허용한다.
      checked(checked(subtotal, "소계") * bpOf(rate), "관리비") / RATE_SCALE
    ),
    "관리비"
  );

/**
 * 관리비율을 만분율(bp) 정수로. **저장되는 요율도 이 값으로 되돌린다.**
 *
 * 계산은 bp 로 하는데 행에는 `0.12345` 가 그대로 굳으면, 화면에 「12.345%」 가
 * 찍히면서 금액은 12.35% 로 나온다 — 5,800만원 견적에서 2,907원이 어긋난다.
 * 받는 쪽에서 눌러 두면 적힌 요율과 받은 금액이 같아진다.
 */
export const bpOf = (rate: number): number => Math.round(rate * RATE_SCALE);

/** bp 로 눌러 둔 요율. 스키마가 저장 전에 이 값으로 바꾼다. */
export const normalizeRate = (rate: number): number =>
  bpOf(rate) / RATE_SCALE;

/** 절사 기본값 — 총액의 만원 미만을 깎는다. 사용자가 편집하면 그 값을 그대로 쓴다. */
export const defaultRounding = (gross: number): number =>
  ((gross % ROUNDING_UNIT) + ROUNDING_UNIT) % ROUNDING_UNIT;

export const calcQuote = (input: CalcQuoteInput): CalcQuoteResult => {
  const trades = input.trades.map(calcTrade);

  const subtotalOf = (group: TradeGroup): number =>
    sum(trades.filter((t) => t.group === group).map((t) => t.amount));

  const subtotal_basic = subtotalOf("BASIC");
  const subtotal_style = subtotalOf("STYLE");
  const mgmt_fee = mgmtFeeOf(subtotal_basic + subtotal_style, input.mgmt_fee_rate);
  // 소계·관리비가 각각 안전해도 **더한 값은 넘을 수 있다.** 마지막 두 수까지
  // 같은 자로 잰다 — 여기까지 봐야 「저장된 총액은 셀 수 있는 수」 가 참이 된다.
  const gross = checked(subtotal_basic + subtotal_style + mgmt_fee, "총액");
  const rounding_adjustment =
    input.rounding_adjustment ?? defaultRounding(gross);

  // 절사액이 gross 를 넘으면 총액이 음수가 된다. 여기서 막아야 저장까지 안 간다.
  if (
    !Number.isInteger(rounding_adjustment) ||
    rounding_adjustment < 0 ||
    rounding_adjustment > gross
  ) {
    throw new Error(
      `절사액은 0 이상 ${formatNumber(gross)} 이하의 정수여야 합니다`
    );
  }

  const vat_mode = input.vat_mode ?? DEFAULT_VAT_MODE;
  const net = checked(gross - rounding_adjustment, "최종 견적금액");
  const split = vat_mode === "EXCL" ? amountsFromSupply(net) : amountsFromTotal(net);
  const cost_total = costSumOf(trades);
  const cost_missing_count = missingOf(trades);
  const profit =
    cost_missing_count > 0
      ? null
      : checked(split.supply_amount - cost_total, "이익");

  return {
    trades,
    subtotal_basic,
    subtotal_style,
    mgmt_fee,
    gross,
    rounding_adjustment,
    total: split.total_amount,
    vat_mode,
    supply_amount: split.supply_amount,
    vat_amount: split.vat_amount,
    cost_total,
    cost_missing_count,
    profit,
    profit_rate_bp:
      profit === null || split.supply_amount === 0
        ? null
        : Math.round(checked(profit * RATE_SCALE, "이익률") / split.supply_amount),
  };
};
